import React from "react"
import { useQuery } from "@tanstack/react-query"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { StatsCard } from "@/components/stats-card"
import { api } from "@/lib/api"
import { BarChart3, Cpu, Activity, Zap, Server } from "lucide-react"

export default function MetricsPage() {
  const { data: realtime } = useQuery({
    queryKey: ["realtime-stats"],
    queryFn: () => api.getRealtimeStats(),
    refetchInterval: 5000,
  })

  const { data: eventStats } = useQuery({
    queryKey: ["event-stats"],
    queryFn: () => api.getEventStats(),
  })

  const system = realtime?.data || {}
  const events = eventStats?.data || {}

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <BarChart3 className="h-8 w-8 text-muted-foreground" />
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Metrics</h1>
          <p className="text-muted-foreground">System metrics and performance</p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatsCard title="CPU Usage" value={`${system.cpu_usage || 0}%`} icon={Cpu} accent="brand" />
        <StatsCard title="Memory" value={`${system.memory_usage || 0}%`} icon={Server} accent="info" />
        <StatsCard title="Active Requests" value={system.active_requests || 0} icon={Activity} accent="success" />
        <StatsCard title="Requests/sec" value={system.requests_per_sec || 0} icon={Zap} accent="warning" />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Event Metrics</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Total Events</span>
              <span className="font-medium">{events.total || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Errors</span>
              <span className="font-medium text-red-500">{events.errors || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Warnings</span>
              <span className="font-medium text-yellow-500">{events.warnings || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Info</span>
              <span className="font-medium">{events.info || 0}</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>System Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Uptime</span>
              <span className="font-medium">{system.uptime || "0h 0m"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Active Connections</span>
              <span className="font-medium">{system.active_connections || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Avg Latency</span>
              <span className="font-medium">{system.avg_latency || 0}ms</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Error Rate</span>
              <span className="font-medium">{system.error_rate || 0}%</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
